import { Button } from "@deere/fuel-react";
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import saveAs from "file-saver";
import { PARTS_TABLE_WORKSHEET_NAME, PARTS_TABLE_SELECTIONS_SHEET_NAME, COLUMN_TYPE, EWDC, PARTS_TABLE_WORKSHEET_NAME_EMPLOYEE } from "../../../utils/surveyLiterals";
import { ERR_SELECT_OPTION, ERR_SHOULD_BE_NUMERIC } from "../../../utils/errorLiterals";
import { generate } from "generate-password";
import { POPUP_QUESTIONS_TITLES, QUESTION_TYPES } from "../../../utils/constants";
import { useTranslation } from "react-i18next";
import { getExcelColumn } from "../../../utils/utils";
import { useSelector } from 'react-redux';

const DownloadPartsTable = ({ data, fixedColumns, questions, rowsToDisable, sampleData, surveyCode }) => {
  const countryList = useSelector(state => state.surveyPartReducer.countryList);
  const countryListWithUnknown = [
    ...(countryList || []),
    { iso2CountryCode: "UN", name: "Unknown" },
  ];
  const unlock = { locked: false, hidden: false };
  const protectionConfig = {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: false,
    formatColumns: false,
    formatRows: false,
    insertRows: false,
    insertColumns: false,
    insertHyperlinks: false,
    deleteRows: false,
    deleteColumns: false,
    sort: false,
    autoFilter: false,
    pivotTables: false,
    spinCount: 100000,
  };
  const columns = [...fixedColumns];
  const { t } = useTranslation();
  questions.forEach((que) => {
    if (!POPUP_QUESTIONS_TITLES.includes(que.type)) {
      if (!que.idQuestionDepends || que.idQuestionDepends === 0) {
        columns.push({ field: "que_" + que.id, headerName: que.text, type: que.type, selections: que.selections.map((sel) => sel.text) });
      } else {
        let parent = questions.find((q) => q.id === que.idQuestionDepends);
        if (parent && parent.type === QUESTION_TYPES.CHEK_POPUP && que.type === COLUMN_TYPE.SELECT) {
          columns.push({ field: "que_" + que.id, headerName: parent.text + " - " + que.text, type: que.type, selections: que.selections.map((sel) => sel.text) });
        } else {
          columns.push({ field: "que_" + que.id, headerName: que.text, type: que.type, selections: que.selections.map((sel) => sel.text) });
        }
      }
    }
  });
  const activeData = data.filter((d) => !rowsToDisable.includes(d.id));

  const setDataValidations = (col) => {
    let objValidation = { type: "", allowBlank: true, showErrorMessage: true, formulae: [], error: "" };
    if (col.type && col.type === COLUMN_TYPE.SELECT) {
      if (col.selections && col.selections.length > 0 && col.selIndex) {
        objValidation.type = "list";
        objValidation.formulae = [`${PARTS_TABLE_SELECTIONS_SHEET_NAME}!$${col.selIndex}$1:$${col.selIndex}$${col.selections.length}`];
        objValidation.error = ERR_SELECT_OPTION;
      }
    } else if (col.type && (col.type === COLUMN_TYPE.NUM || col.type.split("_")[0] === COLUMN_TYPE.NUM)) {
      objValidation.type = "decimal";
      objValidation.operator = "greaterThanOrEqual";
      objValidation.formulae = [0];
      objValidation.error = ERR_SHOULD_BE_NUMERIC;
    } else if (col.type && (col.type === QUESTION_TYPES.SEL_CNTRY || col.type === QUESTION_TYPES.MULT_CNTRY) && countryList && countryList.length > 0 ) {
      objValidation.type = "list";
      objValidation.formulae = [`${PARTS_TABLE_SELECTIONS_SHEET_NAME}!$${col.selIndex}$1:$${col.selIndex}$${countryList.length}`];
      objValidation.error = ERR_SELECT_OPTION;
    } else if (col.type && col.type === QUESTION_TYPES.SEL_CTY_UW &&  countryListWithUnknown && countryListWithUnknown.length > 0) {
      objValidation.type = "list";
      objValidation.formulae = [`${PARTS_TABLE_SELECTIONS_SHEET_NAME}!$${col.selIndex}$1:$${col.selIndex}$${countryListWithUnknown.length}`];
      objValidation.error = ERR_SELECT_OPTION;
    } else {
      return null;
    }
    return objValidation;
  };

  const getSelectionValues = (data) => {
    let objData = { ...data };
    for (const key in objData) {
      if (key.split("_")[0] === "que") {
        let foundQue = questions.find((que) => que.id.toString() === key.split("_")[1]);
        if (foundQue && foundQue.selections.length > 0) {
          const selectedAns = foundQue.selections.find((sel) => sel.value === objData[key]);
          if (selectedAns) objData[key] = selectedAns.text;
        }
      }
    }
    return objData;
  };

  const excelDownload = () => {
    const ExcelJSWorkbook = new ExcelJS.Workbook();
    const worksheet = ExcelJSWorkbook.addWorksheet(surveyCode === EWDC ? PARTS_TABLE_WORKSHEET_NAME_EMPLOYEE : PARTS_TABLE_WORKSHEET_NAME);
    const selectionsSheet = ExcelJSWorkbook.addWorksheet(PARTS_TABLE_SELECTIONS_SHEET_NAME);
    const randomPassword = generate({ length: 10, numbers: true });
    worksheet.protect(randomPassword, protectionConfig);
    selectionsSheet.protect(randomPassword, protectionConfig);
    selectionsSheet.state = 'veryHidden';
    const headerRow = worksheet.addRow();
    let sampleDataRow;
    if(sampleData.length > 0){
      sampleDataRow = worksheet.addRow();
    }
    columns.forEach((col, i) => {
      if (col.type === QUESTION_TYPES.MULT_CNTRY) {
        const startCell = i + 1;
        const endCell = i + 2;
        headerRow.getCell(startCell).value = col.headerName;
        headerRow.getCell(startCell).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "e6e6e6" } };
        headerRow.getCell(startCell).alignment = { vertical: "top", horizontal: "center", wrapText: true };
        headerRow.getCell(startCell).font = { bold: true };
        headerRow.getCell(startCell).border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
        worksheet.getColumn(startCell).width = 15;
        worksheet.getColumn(endCell).width = 15;
        worksheet.mergeCells(headerRow.number, startCell, headerRow.number, endCell);
      } else {
        headerRow.getCell(i + 1).value = col.headerName;
        headerRow.getCell(i + 1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "e6e6e6" } };
        headerRow.getCell(i + 1).alignment = { vertical: "top", horizontal: "center", wrapText: true };
        headerRow.getCell(i + 1).font = { bold: true };
        headerRow.getCell(i + 1).border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
        worksheet.getColumn(i + 1).width = 15;
      }
      if(sampleData.length > 0){
        let data = sampleData.find((sd) => {
          let field = sd.code.split("_")[1];
          if(field === "que") field = sd.code.split("_")[1] + '_' + sd.code.split("_")[2];
          return field === col.field
        });
        sampleDataRow.getCell(i + 1).value = data ? data.text : "";
        sampleDataRow.getCell(i + 1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "e6e6e6" } };
        sampleDataRow.getCell(i + 1).border = { top: {style:'thin'}, left: {style:'thin'}, bottom: {style:'thin'}, right: {style:'thin'} };
      }
      if(col.type === QUESTION_TYPES.SELECT && col.selections.length > 0){
        selectionsSheet.getColumn(i + 1).values = col.selections;
        col.selIndex = getExcelColumn(i+1);
      }else if(col.type === QUESTION_TYPES.SEL_CNTRY && countryList && countryList.length > 0){
        selectionsSheet.getColumn(i + 1).values = countryList.map(c => c.name);
        col.selIndex = getExcelColumn(i+1);
      }else if(col.type === QUESTION_TYPES.SEL_CTY_UW && countryListWithUnknown && countryListWithUnknown.length > 0){
        selectionsSheet.getColumn(i + 1).values = countryListWithUnknown.map(c => c.name);
        col.selIndex = getExcelColumn(i+1);
      }else if(col.type === QUESTION_TYPES.MULT_CNTRY && countryList && countryList.length > 0){
        selectionsSheet.getColumn(i + 1).values = countryList.map(c => c.name);
        col.selIndex = getExcelColumn(i+1);
      }
    });
    activeData.forEach((d) => {
      const dataRow = worksheet.addRow();
      const data = getSelectionValues(d);
      columns.forEach((col, i) => {
        if (col.type === QUESTION_TYPES.MULT_CNTRY) {
          const cell1 = dataRow.getCell(i + 1);
          const cell2 = dataRow.getCell(i + 2);
          const cellValues = data[col.field].split(", ");
          cell1.value = cellValues[0] ? cellValues[0] : "";
          cell2.value = cellValues[1] ? cellValues[1] : "";
          cell1.protection = unlock;
          cell2.protection = unlock;
          let cellValidation = setDataValidations(col);
          if (cellValidation) {
            cell1.dataValidation = cellValidation;
            cell2.dataValidation = cellValidation;
          }
        } else {
          const cell = dataRow.getCell(i + 1);
          cell.value = data[col.field];
          if (col.field.split("_")[0] === "que") cell.protection = unlock;
          let cellValidation = setDataValidations(col);
          if (cellValidation) cell.dataValidation = cellValidation;
        }
      });
    });
    const fileName = surveyCode === EWDC ? PARTS_TABLE_WORKSHEET_NAME_EMPLOYEE : PARTS_TABLE_WORKSHEET_NAME;

    ExcelJSWorkbook.xlsx.writeBuffer().then(function (buffer) {
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), `${fileName}.xlsx`);
    });
  };

  return <Button onClick={excelDownload}>{t('btn_download_tbl_data')}</Button>;
};

export default DownloadPartsTable;
